import { React, useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from "react-bootstrap/Form";
import { Messages } from "primereact/messages";
import { decode as base64_decode, encode as base64_encode } from "base-64";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useTranslation } from "react-i18next";
import * as formik from "formik";
import { ErrorMessage } from "formik";
import * as yup from "yup";
import { T } from "../components/helpful";
import { userTitles } from "./constant";
import { link } from "../config";


/**
 * The RegistrationForm component renders a registration form for users to sign up.
 * @param {boolean} isDisabled - If true, the form is disabled.
 * @param {function} handleDisabled - A function to call when the form is disabled.
 * @returns {ReactElement} The rendered form.
 * @author lionel ghogue ngondeke <lionelladrog@gmail.com>
 */
export default function RegistrationForm({ isDisabled, handleDisabled }) {



  const msgs = useRef(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isloading, setIsloading] = useState(false);
  const user = useSelector((state) => state.user);

  const userGroups = user.userTypes;
  const titles = userTitles;
  const countries = user.countries;
  

  function submitHandler(values) {
    const formData = JSON.stringify({ formData: values });
    setIsloading(true);
    fetch(link + "register", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json; charset=UTF-8",
        Accept: "application/json, text/plain, */*",
      },
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 200) {
          msgs.current.show([
            {
              sticky: true,
              life: 4000,
              severity: "success",
              summary: "",
              detail: "User has been successfully saved.",
              closable: true,
            },
          ]);
        } else {
          msgs.current.show([
            {
              sticky: true,
              life: 4000,
              severity: "error",
              summary: "Error",
              detail: "Error occurred during save.",
              closable: true,
            },
          ]);
        }
        setIsloading(false);
      })
      .catch((error) => {
        console.error("Error:", error);
        setIsloading(false);
      });

    handleDisabled();
  }

  const { Formik } = formik;

  const phoneRegExp = /^(\+\d{1,2}\s?)?(\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}|\d{8,10})$/;

  const schema = yup.object().shape({
    title: yup
      .string()
      .required(<T value={"Title is required"} />)
      .notOneOf(["0"], <T value={"Title is required"} />),
    userType: yup
      .number()
      .required(<T value={"User Type is required"} />)
      .notOneOf([0], <T value={"User Type is required"} />),
    moreInfo: yup.string().optional(),
    address: yup.string().required(<T value={"Address is required"} />),
    firstname: yup.string().required(<T value={"First Name is required"} />),
    lastname: yup.string().required(<T value={"Last Name is required"} />),
    email: yup
      .string()
      .email(<T value={"Invalid email address"} />)
      .required(<T value={"Email is required"} />),
    dateOfBirth: yup
      .string()
      .required(<T value={"Date of Birth is required"} />),
    country: yup
      .number()
      .required(<T value={"Country is required"} />)
      .notOneOf(["0"], <T value={"Country is required"} />),
    company: yup.string().required(<T value={"company is required"} />),
    telephone: yup
      .string()
      .matches(phoneRegExp, <T value={"Telephone number is not valid"} />)
      .required(<T value={"Telephone is required"} />),
    mobile: yup
      .string()
      .matches(phoneRegExp, <T value={"Mobile number is not valid"} />)
      .optional(),
 
    fax: yup
      .string()
      .matches(phoneRegExp, <T value={"Fax number is not valid"} />)
      .optional(),
    
  });

  return (
    <>
      <Messages ref={msgs} />
      <div className="registration-form">
        <div>
          <Formik
            validationSchema={schema}
            onSubmit={submitHandler}
            initialValues={{
              title: "",
              userType: "",
              address: "",
              moreInfo: "",
              firstname: "",
              lastname: "",
              email: "",
              dateOfBirth: "",
              country: "",
              company: "",
              telephone: "",
              mobile: "",
              fax: "",
              unsuscribe: false,
            }}
          >
            {({
              handleSubmit,
              handleChange,
              values,
              errors,
              handleBlur,
              isSubmitting,
            }) => (
              <Form noValidate onSubmit={handleSubmit} className="">
                <Row className={"g-2 " + (isDisabled ? " disabled " : " ")}>
                  <Form.Group as={Col} md="6" controlId="">
                    <FloatingLabel label={<T value={"Country"} />} className="">
                      <Form.Select
                        aria-label=""
                        name="country"
                        className="custom-input"
                        placeholder="Select country"
                        value={values.country}
                        onChange={handleChange}
                        isInvalid={!!errors.country}
                      >
                        <option key={0} value={0}>
                          {"Select country"}
                        </option>
                        {countries.map((country, index) => {
                          return (
                            <option key={index + 1} value={country.id}>
                              {country.description}
                            </option>
                          );
                        })}
                      </Form.Select>
                      <ErrorMessage
                        name="country"
                        component="div"
                        className="invalid-feedback"
                      />
                    </FloatingLabel>
                  </Form.Group>

                  <Form.Group as={Col} md="6" controlId="">
                    <FloatingLabel
                      label={<T value={"User type"} />}
                      className=""
                    >
                      <Form.Select
                        aria-label=""
                        name="userType"
                        className="custom-input"
                        placeholder="Select userType"
                        value={values.userType}
                        onChange={handleChange}
                        isInvalid={!!errors.userType}
                      >
                        <option key={0} value={0}>
                          {"Select user Type"}
                        </option>
                        {userGroups.map((userGroup, index) => {
                          return (
                            <option key={index} value={userGroup.userGroupID}>
                              {userGroup.userGroup}
                            </option>
                          );
                        })}
                      </Form.Select>
                      <ErrorMessage
                        name="type"
                        component="div"
                        className="invalid-feedback"
                      />
                    </FloatingLabel>
                  </Form.Group>

                  <Form.Group as={Col} md="12">
                    <FloatingLabel label={"More Information"} className="">
                      <Form.Control
                        as="textarea"
                        rows={5}
                        id="moreInfo"
                        name="moreInfo"
                        value={values.moreInfo}
                        onChange={handleChange}
                        isInvalid={!!errors.moreInfo}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.moreInfo}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Form.Group>

                  <Form.Group as={Col} md="6" controlId="">
                    <FloatingLabel label={<T value={"Title"} />} className="">
                      <Form.Select
                        aria-label=""
                        id="title"
                        name="title"
                        className="custom-input"
                        placeholder={<T value={"Title"} />}
                        value={values.title}
                        onChange={handleChange}
                        isInvalid={!!errors.title}
                      >
                        <option key={0} value={0}>
                          Select Title
                        </option>
                        {titles.map((title, index) => {
                          return (
                            <option key={index + 1} value={title}>
                              {title}
                            </option>
                          );
                        })}
                      </Form.Select>
                      <ErrorMessage
                        name="title"
                        component="div"
                        className="invalid-feedback"
                      />
                    </FloatingLabel>
                  </Form.Group>
                  <Form.Group as={Col} md="6">
                    <FloatingLabel
                      label={<T value={"First Name"} />}
                      className=""
                    >
                      <Form.Control
                        type="text"
                        id="firstname"
                        className="custom-input"
                        placeholder={<T value={"First Name"} />}
                        name="firstname"
                        value={values.firstname}
                        onChange={handleChange}
                        isInvalid={!!errors.firstname}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.firstname}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Form.Group>
                  <Form.Group as={Col} md="6">
                    <FloatingLabel
                      label={<T value={"Last Name"} />}
                      className=""
                    >
                      <Form.Control
                        type="text"
                        id="lastname"
                        className="custom-input"
                        placeholder={<T value={"Last Name"} />}
                        name="lastname"
                        value={values.lastname}
                        onChange={handleChange}
                        isInvalid={!!errors.lastname}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.lastname}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Form.Group>
                  <Form.Group as={Col} md="6">
                    <FloatingLabel
                      label={<T value={"Date of birth"} />}
                      className=""
                    >
                      <Form.Control
                        type="date"
                        id="dateOfBirth"
                        className="custom-input"
                        name="dateOfBirth"
                        value={values.dateOfBirth}
                        onChange={handleChange}
                        isInvalid={!!errors.dateOfBirth}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.dateOfBirth}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Form.Group>
                  <Form.Group as={Col} md="12">
                    <FloatingLabel label={<T value={"Email"} />} className="">
                      <Form.Control
                        type="email"
                        id="email"
                        className="custom-input"
                        placeholder={<T value={"Email"} />}
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        isInvalid={!!errors.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Form.Group>
                  <Form.Group as={Col} md="12">
                    <FloatingLabel label={<T value={"Address"} />} className="">
                      <Form.Control
                        as="textarea"
                        rows={5}
                        id="address"
                        name="address"
                        value={values.address}
                        onChange={handleChange}
                        isInvalid={!!errors.address}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.address}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Form.Group>

                  <Form.Group as={Col} md="6">
                    <FloatingLabel label={<T value={"Company"} />} className="">
                      <Form.Control
                        type="text"
                        id="company"
                        className="custom-input"
                        placeholder=""
                        name="company"
                        value={values.company}
                        onChange={handleChange}
                        isInvalid={!!errors.company}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.company}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Form.Group>
                  <Form.Group as={Col} md="6">
                    <FloatingLabel
                      label={<T value={"Telephone"} />}
                      className=""
                    >
                      <Form.Control
                        type="text"
                        id="telephone"
                        className="custom-input"
                        placeholder={<T value={"Telephone"} />}
                        name="telephone"
                        value={values.telephone}
                        onChange={handleChange}
                        isInvalid={!!errors.telephone}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.telephone}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Form.Group>

                  <Form.Group as={Col} md="6">
                    <FloatingLabel label={<T value={"Mobile"} />} className="">
                      <Form.Control
                        type="text"
                        id="mobile"
                        className="custom-input"
                        placeholder={<T value={"Mobile"} />}
                        name="mobile"
                        value={values.mobile}
                        onChange={handleChange}
                        isInvalid={!!errors.mobile}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.mobile}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Form.Group>

                  <Form.Group as={Col} md="6">
                    <FloatingLabel label={<T value={"Fax"} />} className="">
                      <Form.Control
                        type="text"
                        id="fax"
                        placeholder={<T value={"Fax"} />}
                        name="fax"
                        className="custom-input"
                        value={values.fax}
                        onChange={handleChange}
                        isInvalid={!!errors.fax}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.fax}
                      </Form.Control.Feedback>
                    </FloatingLabel>
                  </Form.Group>
                  <div>
                    <input
                      type="checkbox"
                      id="unsuscribe"
                      name="unsuscribe"
                      checked={values.unsuscribe}
                      onChange={handleChange}
                      className="me-3"
                    />
                    <label htmlFor="unsuscribe">
                      I want to receive notification emails
                      <span className=""> </span>
                    </label>
                  </div>
                </Row>

                <div className="row justify-content-center  text-uppercase">
                  <button
                    type="submit"
                    disabled={isDisabled}
                    className={
                      "col-12 col-sm-4 col-lg-4  btn  bcb-btn-primary bcb-btn-small mt-3 text-uppercase "
                    }
                  >
                    {isloading ? (
                      <>
                        <span
                          className="spinner-grow spinner-grow-sm"
                          aria-hidden="true"
                        ></span>
                        <span className="ml-2">Saving...</span>
                      </>
                    ) : (
                      <T value={"Register"} />
                    )}
                    
                    {/* {<T value={"Register"} />}{" "} */}
                  </button>
                  <div className="text-center">
                    <a
                      href="/"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/");
                      }}
                      className=" bcb-secondary-light-color text-smaller  sign-in-btn"
                    >
                      Sign in
                      {/* {t("Sign in")} */}
                    </a>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
}
